import { useEffect, useState, useRef, useCallback } from 'react';
import { trackPageView, trackCTA } from '../tracking/pixels';

/** Pause auto-scroll for this many ms after user scrolls */
const CAROUSEL_PAUSE_MS = 2500;
/** Auto-scroll speed in px per frame */
const CAROUSEL_SPEED = 0.8;

/**
 * SplashPage Component
 * Modern splash page with slanted phone mockups and formatted content
 */
export const SplashPage = () => {
  // Email form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting] = useState(false);
  const [submitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Carousel: manual scroll pauses auto-scroll
  const carouselRef = useRef<HTMLDivElement>(null);
  const pauseUntilRef = useRef(0);
  const rafRef = useRef<number>(0);

  // Promo videos: play button overlay; tap starts video with sound
  const promoVideo1Ref = useRef<HTMLVideoElement>(null);
  const promoVideo2Ref = useRef<HTMLVideoElement>(null);
  const [video1Playing, setVideo1Playing] = useState(false);
  const [video2Playing, setVideo2Playing] = useState(false);
  const [video1Paused, setVideo1Paused] = useState(false);
  const [video2Paused, setVideo2Paused] = useState(false);

  const pauseAutoScroll = useCallback(() => {
    pauseUntilRef.current = Date.now() + CAROUSEL_PAUSE_MS;
  }, []);

  // Track page view on component mount
  useEffect(() => {
    trackPageView();
  }, []);

  // Carousel: infinite loop when scroll passes midpoint (only wrap right-edge so we don’t fight auto-scroll)
  const onCarouselScroll = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const half = max * 0.5;
    if (max <= 0 || half <= 0) return;
    requestAnimationFrame(() => {
      if (el.scrollLeft >= half - 2) el.scrollLeft -= half;
    });
  }, []);

  // Carousel: auto-scroll (starts after layout is ready)
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    const tick = () => {
      if (Date.now() < pauseUntilRef.current) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      el.scrollLeft += CAROUSEL_SPEED;
      const half = max * 0.5;
      if (half > 0 && el.scrollLeft >= half - 2) el.scrollLeft -= half;
      rafRef.current = requestAnimationFrame(tick);
    };
    const start = () => {
      rafRef.current = requestAnimationFrame(tick);
    };
    requestAnimationFrame(start);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    el.addEventListener('scroll', onCarouselScroll, { passive: true });
    return () => el.removeEventListener('scroll', onCarouselScroll);
  }, [onCarouselScroll]);

  // Tap play overlay: start video with sound (user gesture), hide overlay
  const handlePromoPlay = useCallback((which: 1 | 2) => {
    const video = which === 1 ? promoVideo1Ref.current : promoVideo2Ref.current;
    if (!video) return;
    video.volume = 1;
    video.muted = false;
    video.play().catch(() => {});
    if (which === 1) {
      setVideo1Playing(true);
      setVideo1Paused(false);
    } else {
      setVideo2Playing(true);
      setVideo2Paused(false);
    }
  }, []);

  // Capture first frame as poster so thumbnail shows (mobile often doesn't paint first frame otherwise)
  useEffect(() => {
    const capturePoster = (video: HTMLVideoElement) => {
      if (video.readyState < 2) return;
      try {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (w === 0 || h === 0) return;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(video, 0, 0, w, h);
        video.poster = canvas.toDataURL('image/jpeg', 0.85);
      } catch {
        // ignore
      }
    };
    const setupVideo = (video: HTMLVideoElement) => {
      const onLoaded = () => capturePoster(video);
      if (video.readyState >= 2) capturePoster(video);
      else video.addEventListener('loadeddata', onLoaded, { once: true });
    };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const wrapper = entry.target as HTMLElement;
          const video = wrapper.querySelector('video');
          if (video && !video.poster) video.load();
        });
      },
      { rootMargin: '100px', threshold: 0 }
    );
    const id = setTimeout(() => {
      const v1 = promoVideo1Ref.current;
      const v2 = promoVideo2Ref.current;
      if (v1) {
        setupVideo(v1);
        v1.load();
        const wrap1 = v1.closest('.promo-video-container');
        if (wrap1) observer.observe(wrap1);
      }
      if (v2) {
        setupVideo(v2);
        v2.load();
        const wrap2 = v2.closest('.promo-video-container');
        if (wrap2) observer.observe(wrap2);
      }
    }, 100);
    return () => {
      clearTimeout(id);
      observer.disconnect();
    };
  }, []);

  // Pause promo videos when user scrolls away
  useEffect(() => {
    let cleanup: (() => void) | undefined;
    const id = setTimeout(() => {
      const v1 = promoVideo1Ref.current;
      const v2 = promoVideo2Ref.current;
      const containers = [v1?.closest('.promo-video-container'), v2?.closest('.promo-video-container')].filter(Boolean) as HTMLElement[];
      if (containers.length === 0) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = (entry.target as HTMLElement).querySelector('video');
            if (!video || entry.isIntersecting) return;
            video.pause();
            if (video === v1) setVideo1Paused(true);
            if (video === v2) setVideo2Paused(true);
          });
        },
        { threshold: 0, rootMargin: '0px' }
      );
      containers.forEach((el) => observer.observe(el));
      cleanup = () => observer.disconnect();
    }, 150);
    return () => {
      clearTimeout(id);
      cleanup?.();
    };
  }, []);

  // (Removed unused expand state and handlers)

  // Handle CTA button click
  const handleCTAClick = () => {
    trackCTA('subscribe');
    // Scroll to App Store section
    document.getElementById('app-store')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle email form submission (for mailing list/retargeting)
  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const emailValue = email?.trim() || '';
    const nameValue = name?.trim() || '';

    const mailtoLink = `mailto:mygiggenie@gmail.com?subject=Save my email for mailing list&body=Name: ${encodeURIComponent(nameValue)}%0AEmail: ${encodeURIComponent(emailValue)}`;
    window.location.href = mailtoLink;

    setEmail('');
    setName('');
  };

  // USER ASSETS – DO NOT DELETE OR REPLACE
  // Originals stored in /assests (read-only)

  return (
    <div className="splash-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          {/* Left: Text Content */}
          <div className="hero-content">
            <div className="logo-container">
              <img src="/GigGenieLogo2.png" alt="Gig Genie Logo" className="logo" />
            </div>
            
            <h1 className="hero-heading">
              The ultimate tool every gig worker should have in their toolbox!
            </h1>
            
            <p className="hero-description">
              Built by a Gig worker for Gig workers! Uber / Lyft drivers, DoorDashers, Barbers, Street Vendors, Bartenders, DJ's, Musicians, Artists, Independent Contractors, or anyone who relies on unpredictable day-to-day income to make ends meet.
            </p>
            
            <p className="hero-description">
              If your income looks different every day..
              <br />
              Gig Genie was built for you!
            </p>
            
            <button 
              className="cta-button" 
              onClick={handleCTAClick}
              aria-label="Get Started"
            >
              Get Started
            </button>
          </div>

          {/* Right: Intro + Screenshots Carousel */}
          <div className="screenshots-section">
            <div className="screenshots-intro">
              <h2 className="screenshots-headline">Gig Genie<br />Smarter than any other finance / tracking app for Gig workers on the market!</h2>
              <p className="screenshots-body">
                Unlike other gig tracking and finance apps that just show numbers, Gig Genie not only understands your bills, but it understands exactly how you make your day to day income, whether you're driving for Uber, DoorDash'n, or running your own private hustle. Your bills talk to your income in the background and tell you how much you need to earn from any point in the day, week, or month. With Gig Genie's many smart features all communicating with each other in real time, you never have to deal with calculators, spreadsheets, or guessing numbers. Gig Genie's already pre calculated everything for you, so you can work LESS and make MORE!
              </p>
              <p className="screenshots-glimpse">Here's a quick glimpse!</p>
            </div>
            <div className="screenshots-carousel-outer">
            <div className="screenshots-carousel-fade-left" aria-hidden />
            <div className="screenshots-carousel-fade-right" aria-hidden />
            <div
              className="screenshots-carousel-wrapper"
              ref={carouselRef}
              onWheel={(e) => {
                pauseAutoScroll();
                const el = carouselRef.current;
                if (el) {
                  el.scrollLeft += e.deltaX || e.deltaY;
                  if (e.deltaX || e.deltaY) e.preventDefault();
                }
              }}
              onTouchStart={pauseAutoScroll}
              onTouchMove={pauseAutoScroll}
            >
              <div className="screenshots-carousel">
                {/* Screenshots 01–02 */}
                <div className="screenshots-grid">
                  <img src="/iPhones%20%206.9-01.png" alt="Gig Genie - Screenshot 1" className="screenshot" />
                  <img src="/iPhones%20%206.9-02.png" alt="Gig Genie - Screenshot 2" className="screenshot" />
                </div>
                {/* Screenshots 03–04 */}
                <div className="screenshots-grid">
                  <img src="/iPhones%20%206.9-03.png" alt="Gig Genie - Screenshot 3" className="screenshot" />
                  <img src="/iPhones%20%206.9-04.png" alt="Gig Genie - Screenshot 4" className="screenshot" />
                </div>
                {/* Screenshots 05–06 */}
                <div className="screenshots-grid">
                  <img src="/iPhones%20%206.9-05.png" alt="Gig Genie - Screenshot 5" className="screenshot" />
                  <img src="/iPhones%20%206.9-06.png" alt="Gig Genie - Screenshot 6" className="screenshot" />
                </div>
                {/* Screenshots 07–08 */}
                <div className="screenshots-grid">
                  <img src="/iPhones%20%206.9-07.png" alt="Gig Genie - Screenshot 7" className="screenshot" />
                  <img src="/iPhones%20%206.9-08.png" alt="Gig Genie - Screenshot 8" className="screenshot" />
                </div>
                {/* Screenshots 09–10 */}
                <div className="screenshots-grid">
                  <img src="/iPhones%20%206.9-09.png" alt="Gig Genie - Screenshot 9" className="screenshot" />
                  <img src="/iPhones%20%206.9-10.png" alt="Gig Genie - Screenshot 10" className="screenshot" />
                </div>
                {/* Duplicate for infinite loop */}
                <div className="screenshots-grid">
                  <img src="/iPhones%20%206.9-01.png" alt="Gig Genie - Screenshot 1" className="screenshot" />
                  <img src="/iPhones%20%206.9-02.png" alt="Gig Genie - Screenshot 2" className="screenshot" />
                </div>
                <div className="screenshots-grid">
                  <img src="/iPhones%20%206.9-03.png" alt="Gig Genie - Screenshot 3" className="screenshot" />
                  <img src="/iPhones%20%206.9-04.png" alt="Gig Genie - Screenshot 4" className="screenshot" />
                </div>
                <div className="screenshots-grid">
                  <img src="/iPhones%20%206.9-05.png" alt="Gig Genie - Screenshot 5" className="screenshot" />
                  <img src="/iPhones%20%206.9-06.png" alt="Gig Genie - Screenshot 6" className="screenshot" />
                </div>
                <div className="screenshots-grid">
                  <img src="/iPhones%20%206.9-07.png" alt="Gig Genie - Screenshot 7" className="screenshot" />
                  <img src="/iPhones%20%206.9-08.png" alt="Gig Genie - Screenshot 8" className="screenshot" />
                </div>
                <div className="screenshots-grid">
                  <img src="/iPhones%20%206.9-09.png" alt="Gig Genie - Screenshot 9" className="screenshot" />
                  <img src="/iPhones%20%206.9-10.png" alt="Gig Genie - Screenshot 10" className="screenshot" />
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* Promo Video 1 - between hero and features */}
      <section className="promo-videos-section">
        <div className="promo-videos-container">
          <h2 className="promo-videos-headline">See Gig Genie in Action</h2>
          <div className="promo-video-wrapper promo-video-container">
            <video
              ref={promoVideo1Ref}
              src="/Most%20Apps%201%20Promo.MP4"
              className="promo-video"
              loop
              playsInline
              preload="auto"
              aria-label="Gig Genie Promo 1"
            />
            {(!video1Playing || video1Paused) && (
              <button
                type="button"
                className="promo-video-play-overlay"
                onClick={() => handlePromoPlay(1)}
                aria-label="Play video with sound"
              >
                <span className="promo-video-play-button" aria-hidden />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* App Store Download - below first promo video */}
      <section className="app-store-section">
        <div className="app-store-container">
          <div className="app-store-content">
            <h2 className="app-store-title">Download Gig Genie</h2>
            <p className="app-store-subtitle">
              Now available on the App Store. Built for the hustle — start managing your gig income today.
            </p>
            <a
              href="https://apps.apple.com/us/app/gig-genie/id6756893385"
              target="_blank"
              rel="noopener noreferrer"
              className="app-store-badge-link"
              aria-label="Download Gig Genie on the App Store"
            >
              <img
                src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
                alt="Download on the App Store"
                className="app-store-badge"
              />
            </a>
            <p className="app-store-note">Free to download</p>
          </div>
        </div>
      </section>

      {/* What It Does Section */}
      <section className="features-section">
        <div className="features-container">
          <h2 className="section-title">Your bills speak, your income listens..</h2>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🧠</div>
              <h3 className="feature-title">Bills Intelligence</h3>
              <p className="feature-description">
                Your Bills ask your Income how your daily, weekly, monthly progress is coming along, while Gig Genie pre-calculates the math, and tells you the exact numbers you need to hit every day, week, or month, in order to cover those bills on time. It’s all done automatically, and silently in the background for you while you focus on your grind. Some days you’ll need to make less than others based on your progress. Your bills know exactly how much they need at any point in the month, based on real data in real time.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">💰</div>
              <h3 className="feature-title">Log your income. That’s it!</h3>
              <p className="feature-description">
                Every dollar auto-allocates to your most urgent bills or goals so you never have to wonder what’s covered and what still needs to be covered. No math homework, no guesswork. It’s all pre-calculated for you automatically!
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🚗</div>
              <h3 className="feature-title">Private session tracking</h3>
              <p className="feature-description">
                Gig Genie automatically calculates your preset rates for your miles driven, hours worked or both. Whether you're moving a person, a package, food, cargo etc. What you earn from that trip automatically updates your entire financial system and allocates to your most urgent bills.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🛞</div>
              <h3 className="feature-title">Mileage Calculator</h3>
              <p className="feature-description">
                Enter your clients start and end locations, add your rates per mile, hour, or both, and Gig Genie does the rest! Whether you’re transporting a person, package, or cargo. It’s all pre-calculated for you. Just push what you earned to your financial system, and your upcoming due bills etc are all updated in real time.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🧮</div>
              <h3 className="feature-title">Smart Calculator</h3>
              <p className="feature-description">
                Use it as your go-to calculator, but when you get your total, instead of having to write it down. Just push it directly to income or expenses. That’s it! Your entire system updates automatically. No writing anything down, no figuring out where numbers need to go, no spreadsheets or whiteboards.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">📇</div>
              <h3 className="feature-title">Digital business cards</h3>
              <p className="feature-description">
                Create and share personalized business cards anytime! No printing, no hassle. Flip a toggle, choose your service, and share instantly for any gig you’re offering at that time.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🧾</div>
              <h3 className="feature-title">Automatic tax tracking</h3>
              <p className="feature-description">
                All earnings are automatically itemized in the background so they’re ready for tax season - no spreadsheets or receipts needed. Download your general PDF or pre-auto-calculated PDF with specific breakdowns to everything neat and clean for tax season. It’s all done in the background. You do nothing but earn and track!
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🔍</div>
              <h3 className="feature-title">Real-time monitoring</h3>
              <p className="feature-description">
                Your income feeds your bills in real time as you make it. Every gig, payout, and session is assigned to what matters most in that very moment.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3 className="feature-title">Always know where you stand</h3>
              <p className="feature-description">
                See exactly how much you’ve made today, this week, or this month and what still needs to be covered. No surprises at the end of the month.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🏁</div>
              <h3 className="feature-title">Know when you’re done</h3>
              <p className="feature-description">
                Gig Genie looks at your bills, your deadlines, and today’s progress, then tells you when you can turn off your gig apps and go home.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">💵</div>
              <h3 className="feature-title">Built for the hustle</h3>
              <p className="feature-description">
                If your income changes day to day but your bills don’t, Gig Genie is your wish come true.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Promo Video 2 - lower on page, after features */}
      <section className="promo-video2-section">
        <div className="promo-video2-container">
          <h2 className="promo-videos-headline">More of Gig Genie</h2>
          <div className="promo-video-wrapper promo-video-container">
            <video
              ref={promoVideo2Ref}
              src="/Most%20Apps%202%20Promo.MP4"
              className="promo-video"
              loop
              playsInline
              preload="auto"
              aria-label="Gig Genie Promo 2"
            />
            {(!video2Playing || video2Paused) && (
              <button
                type="button"
                className="promo-video-play-overlay"
                onClick={() => handlePromoPlay(2)}
                aria-label="Play video with sound"
              >
                <span className="promo-video-play-button" aria-hidden />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* App Store Download - above bio */}
      <section className="app-store-section">
        <div className="app-store-container">
          <div className="app-store-content">
            <h2 className="app-store-title">Download Gig Genie</h2>
            <p className="app-store-subtitle">
              Now available on the App Store. Built for the hustle — start managing your gig income today.
            </p>
            <a
              href="https://apps.apple.com/us/app/gig-genie/id6756893385"
              target="_blank"
              rel="noopener noreferrer"
              className="app-store-badge-link"
              aria-label="Download Gig Genie on the App Store"
            >
              <img
                src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
                alt="Download on the App Store"
                className="app-store-badge"
              />
            </a>
            <p className="app-store-note">Free to download</p>
          </div>
        </div>
      </section>

      {/* About Creator Section */}
      <section className="about-section">
        <div className="about-container">
          <div className="about-header">
            <h2 className="about-title">How Gig Genie Was Born</h2>
          </div>
          
          <div className="about-creator">
            <div className="creator-content">
              <div className="creator-image-container">
                <img src="/IMG_4065.JPG" alt="Anwaar" className="creator-photo" />
              </div>
              <div className="creator-text">
                <h3 className="creator-subtitle">About Anwaar</h3>
                <p className="creator-location">Creator & CEO of Gig Genie</p>
                
                <h4 className="creator-section-title">I Just Couldn't Take It Anymore 😩</h4>
                <p className="creator-story">
                  I started driving for Uber after quitting my job as an Amazon driver, and was making some good money at the time. As earnings decreased, it became harder to keep track of what I was making, and bills were coming up short at the end of the month. I was having to go through all of my bills and expenses, and calculate that based on what I made for the day, whether it was $200 or $20. I had no idea what bills were being covered or where to apply my earnings without lots of bills vs. earnings calculations, whiteboards, receipts, etc. It was a stressful mess. That’s when I got the idea: what if I could see how much I needed to make any day of the week or month, and it would align with the bills I had due so I didn’t overwork myself and everything got paid? That’s when Gig Genie was born. I taught myself how to build apps and sacrificed almost 2 years of my life to bring Gig Genie to life.
                </p>
                <p className="creator-story">
                  I've been doing gig work for over 20 years, DJ'ing, Uber, Lyft, Instacart, Amazon Flex, you name it. I totally rely on daily income from gig apps to make ends meet, and got tired of hitting the end of the month and realizing I came up short. Gas here, lunch there, subscriptions, random bills, I was never where I thought I'd be at the end of the month.
                </p>
                <p className="creator-story">
                  I knew how much I needed to make per month, but then I'm like, “Okay, well how much is that per week?” “How much do I need to make per day to make that number per week?” “How do I even calculate this?” “How many days do I need to work?” “How many hours?” Then if I missed a goal one day, I had to recalculate everything from scratch. So exhausting, especially just for it to be wrong at the end of the month. Constantly using a calculator, writing stuff down, trying to track it all, it got overwhelming.
                </p>
                <p className="creator-story">
                  So I decided to learn how to build apps and put all of that into one system. A smart system that solves all these problems automatically in the background while I focus on my hustle.
                </p>
                <p className="creator-mission">
                  This comes from real pain points, not some office idea. I know the hustle because I've lived it. GigGenie tells me exactly what I need to make each day, week, and month. It knows my bills, my income, my schedule, and still tells me what to do. If I fall short, it flags it and tells you exactly what you need to do from any point of the day, week, or month to make sure ends meet on time with you doing the least amount of work.
                </p>
                <p className="creator-mission">
                  Now my days are easier. I know when I need to push harder and when I don't. I can focus on my work because GigGenie keeps everything on track.
                </p>
                <p className="creator-mission">
                  Give GigGenie a try and see what it can do for you. From one gig worker to another, this app learns how you work and makes your days way easier.
                </p>
                <p className="creator-mission">
                  Work Less, Earn More!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* App Store Download Section */}
      <section id="app-store" className="app-store-section">
        <div className="app-store-container">
          <div className="app-store-content">
            <h2 className="app-store-title">Download Gig Genie</h2>
            <p className="app-store-subtitle">
              Now available on the App Store. Built for the hustle — start managing your gig income today.
            </p>
            <a
              href="https://apps.apple.com/us/app/gig-genie/id6756893385"
              target="_blank"
              rel="noopener noreferrer"
              className="app-store-badge-link"
              aria-label="Download Gig Genie on the App Store"
            >
              <img
                src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
                alt="Download on the App Store"
                className="app-store-badge"
              />
            </a>
            <p className="app-store-note">Free to download</p>
          </div>
        </div>
      </section>

      {/* Email Collection Section (for mailing list/retargeting) */}
      <section id="email-signup" className="email-signup-section">
        <div className="email-container">
          <div className="email-content">
            <h2 className="email-title">Get Updates & Exclusive Tips</h2>
            <p className="email-subtitle">
              Join thousands of gig workers who are taking control of their finances. 
              Get notified about new features and receive exclusive tips for maximizing your gig income.
            </p>
            
            <form className="email-form" onSubmit={handleEmailSubmit}>
              <div className="form-group">
                <input
                  type="text"
                  className="email-input"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isSubmitting}
                />
                <input
                  type="email"
                  className="email-input"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isSubmitting}
                />
                <button 
                  type="submit" 
                  className="email-submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Get Updates'}
                </button>
              </div>
              
              {submitStatus === 'success' && (
                <p className="form-message form-success">
                  ✅ Thanks! You've been added to our mailing list.
                </p>
              )}
              
              {submitStatus === 'error' && (
                <p className="form-message form-error">
                  ⚠️ Please enter a valid email address.
                </p>
              )}
            </form>
            
            <p className="email-privacy">
              🔒 We respect your privacy. Unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>

      {/* Footer with Humble Stack Logo */}
      <footer className="splash-footer">
        <div className="footer-container">
          <img 
            src="/humblestack.png" 
            alt="Humble Stack" 
            className="footer-logo"
          />
          <a href="/privacy-policy" className="footer-link">Privacy Policy</a>
          <a href="/terms" className="footer-link">Terms of Use</a>
          <a href="/support" className="footer-link">Support</a>
        </div>
      </footer>
    </div>
  );
};
